import { objectToQueryString, HTTPError, booleanToString } from './libs';
import {
  IamAliveResponse,
  CasClientConfig,
  GetFormDefaultsParams,
  InitParams,
  LoginParams,
  EndpointParams,
  TokenLoginParams,
  GetFormDefaultsResponse,
  LoginResponse,
  LogoutResponse,
} from './types';

export class CasClient {
  private readonly casUrl: string;
  private readonly serviceUrl: string;
  private readonly language: string;
  private readonly enableFlagHandling?: boolean;
  private readonly enableRecaptcha?: boolean;
  private executionKey?: string;
  private loginTicket?: string;
  private eventId?: string;

  constructor({ casUrl, serviceUrl, language, enableFlagHandling, enableRecaptcha }: CasClientConfig) {
    if (!casUrl || !serviceUrl) {
      throw new Error('casUrl and serviceUrl are required');
    }

    this.casUrl = casUrl.replace(/\/+$/, '');
    this.serviceUrl = serviceUrl;
    this.language = language ?? 'ql';
    this.enableFlagHandling = enableFlagHandling;
    this.enableRecaptcha = enableRecaptcha;
  }

  async init(params?: InitParams, serviceUrl?: string): Promise<LoginResponse> {
    const endpoint = this.getEndpoint({ path: 'login', serviceUrl: serviceUrl || this.serviceUrl, ...params });
    const response = await fetch(endpoint, this.createGetOptions());

    if (!response.ok) {
      throw new HTTPError(response);
    }

    const responseData = await response.json();

    return responseData.success ? responseData : Promise.reject();
  }

  async getFormDefaults(params?: GetFormDefaultsParams): Promise<GetFormDefaultsResponse> {
    const endpoint = this.getEndpoint({ path: 'login', serviceUrl: this.serviceUrl, ...params }, true);
    const response = await fetch(endpoint, this.createGetOptions());

    if (!response.ok) {
      return Promise.reject();
    }

    const responseData = await response.json();

    if (responseData && responseData.form_defaults) {
      const { executionKey, lt, _eventId } = responseData.form_defaults;

      this.executionKey = executionKey;
      this.loginTicket = lt;
      this.eventId = _eventId;

      return responseData;
    }

    // If there's no form_defaults treat anything with success: true
    // as 'Already logged in' case but reject with message
    if (responseData && responseData.success) {
      return Promise.reject(responseData);
    }

    return Promise.reject();
  }

  async login({
    username,
    password,
    rememberMe,
    rememberUsername,
    custLogin,
    renew,
    token,
  }: LoginParams): Promise<LoginResponse> {
    const endpoint = this.getEndpoint({ path: 'login', serviceUrl: this.serviceUrl, custLogin, renew }, true);
    const response = await fetch(
      endpoint,
      this.createPostOptions({
        body: objectToQueryString({
          username,
          password,
          rememberMe,
          rememberUsername,
          _rememberUsername: booleanToString(rememberUsername),
          _rememberMe: booleanToString(rememberMe),
          ...(this.executionKey && { execution: this.executionKey }),
          ...(this.loginTicket && { lt: this.loginTicket }),
          ...(this.eventId && { _eventId: this.eventId }),
        }),
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          ...(token && { 'X-Captcha-Token': token }),
        },
      })
    );

    if (!response.ok) {
      throw new HTTPError(response);
    }

    const responseData = await response.json();

    return responseData.success ? responseData : Promise.reject(responseData);
  }

  async tokenLogin({ token, renew }: TokenLoginParams): Promise<LoginResponse> {
    const endpoint = this.getEndpoint({ path: 'tokenlogin', renew });
    const response = await fetch(
      endpoint,
      this.createPostOptions({
        body: JSON.stringify({ token }),
        headers: {
          'Content-Type': 'application/json',
        },
      })
    );

    if (!response.ok) {
      throw new HTTPError(response);
    }

    const responseData = await response.json();

    return responseData.success ? responseData : Promise.reject(responseData);
  }

  async logout(): Promise<LogoutResponse> {
    const endpoint = this.getEndpoint({ path: 'logout' });
    const response = await fetch(endpoint, this.createGetOptions());

    if (!response.ok) {
      throw new HTTPError(response);
    }

    const responseData = await response.json();

    return responseData.logoutSuccess ? responseData : Promise.reject();
  }

  async imAlive(): Promise<IamAliveResponse> {
    const endpoint = this.getEndpoint({ path: 'imAlive' });
    const response = await fetch(endpoint, this.createGetOptions());

    if (!response.ok) {
      throw new HTTPError(response);
    }

    try {
      const responseData = await response.json();
      const { timeToKill, expiresAt } = responseData;

      return {
        imAlive: response.ok,
        timeToKill,
        expiresAt,
      };
    } catch (error) {
      return {
        imAlive: response.ok,
        timeToKill: -1,
        expiresAt: -1,
      };
    }
  }

  private mapFlagsToVersionSegment(): string {
    if (this.enableRecaptcha) {
      return '/v3';
    }

    if (this.enableFlagHandling) {
      return '/v2';
    }

    return '';
  }

  private getEndpoint(
    { path, serviceUrl, custLogin, gateway, renew }: EndpointParams,
    useFeatureFlags: boolean = false
  ) {
    const versionSegment = useFeatureFlags ? this.mapFlagsToVersionSegment() : '';
    const params = [
      serviceUrl ? `service=${encodeURIComponent(serviceUrl)}` : null,
      gateway ? 'gateway=true' : null,
      custLogin ? 'cust_login=true' : null,
      renew ? 'renew=true' : null,
    ]
      .filter((param) => param !== null)
      .join('&');
    const query = params ? `?${params}` : '';

    return `${this.casUrl}${versionSegment}/${path}${query}`;
  }

  private createGetOptions(): RequestInit {
    return {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Cache-Control': 'no-store',
      },
      credentials: 'include',
    };
  }

  private createPostOptions({ body, headers }: { body: BodyInit; headers?: Record<string, string> }): RequestInit {
    return {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Accept-Language': this.language,
        'Cache-Control': 'no-store',
        ...headers,
      },
      body,
      credentials: 'include',
    };
  }
}

const instances = new Map();

export const createCasClient = (config: CasClientConfig): CasClient => {
  const instanceKey = `${config.casUrl}:${config.serviceUrl}:${config.language}`;

  if (!instances.has(instanceKey)) {
    instances.set(instanceKey, new CasClient(config));
  }

  return instances.get(instanceKey);
};
